'use strict';

/**
 * Progressive image loading via event delegation.
 *
 * Handles load/error events for `.image` components:
 * 1. Blurry placeholder fades in when loaded
 * 2. Main image fades in, blurry hides after transition
 * 3. Error state shows alt text with dashed border
 */

/**
 * Applies loaded-state styles to an image and its container.
 * @param {HTMLImageElement} img - The image element to handle.
 * @private
 */
function _handleImageLoad(img) {
    let container = img.closest('.image');
    if (!container) return;

    if (img.classList.contains('image__blurry')) {
        img.style.setProperty('--image-opacity', '1');
    }

    if (img.classList.contains('image__main')) {
        img.style.setProperty('--image-opacity', '1');
        container.classList.remove('image--loading');

        let blurry = container.querySelector('.image__blurry');
        if (blurry) {
            blurry.style.setProperty('--blurry-visibility', 'hidden');
            blurry.style.setProperty('--visibility-delay', '0.5s');
        }
    }
}

/**
 * Applies error-state styles to an image and its container.
 * @param {HTMLImageElement} img - The image element to handle.
 * @private
 */
function _handleImageError(img) {
    let container = img.closest('.image');
    if (!container) return;

    img.style.setProperty('--image-opacity', '1');
    container.classList.remove('image--loading');
    container.classList.add('image--error');
}

/**
 * Registers image load/error delegation and hydrates already-loaded images.
 */
export function initImageProgressiveLoading() {
    document.addEventListener(
        'load',
        (e) => {
            let target = e.target;
            if (!(target instanceof HTMLImageElement)) return;
            if (!target.closest('.image')) return;
            _handleImageLoad(target);
        },
        true,
    );

    document.addEventListener(
        'error',
        (e) => {
            let target = e.target;
            if (!(target instanceof HTMLImageElement)) return;
            if (!target.classList.contains('image__main')) return;
            _handleImageError(target);
        },
        true,
    );

    document.querySelectorAll('.image img').forEach((img) => {
        if (img.complete) {
            if (img.naturalWidth > 0) {
                _handleImageLoad(img);
            } else if (img.classList.contains('image__main')) {
                _handleImageError(img);
            }
        }
    });
}
